import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const memoryTypes = [
  { value: 'call', label: 'Phone Call' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'note', label: 'Note' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'whatsapp', label: 'WhatsApp' }
];

const importanceLevels = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' }
];

export default function AddMemoryPage() {
  const { personId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'note',
    summary: '',
    memory_text: '',
    contact_name: '',
    source: 'manual',
    importance: 'medium',
    keywords: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.summary || !formData.memory_text) {
      toast.error('Please fill in summary and content');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
      };

      await axios.post(`${API}/persons/${personId}/memories`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Memory added successfully');
      navigate(`/clones/${personId}/chat`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add memory');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout title="Add Memory">
      <div className="max-w-2xl">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/clones/${personId}/chat`)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-xl font-heading font-bold">Add New Memory</h2>
            <p className="text-sm text-muted-foreground">Create a manual memory entry for the clone</p>
          </div>
        </div>

        <Card data-testid="add-memory-card">
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="type">Type *</Label>
                  <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                    <SelectTrigger data-testid="memory-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {memoryTypes.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="importance">Importance</Label>
                  <Select value={formData.importance} onValueChange={(value) => setFormData({ ...formData, importance: value })}>
                    <SelectTrigger data-testid="memory-importance-select">
                      <SelectValue placeholder="Select importance" />
                    </SelectTrigger>
                    <SelectContent>
                      {importanceLevels.map(level => (
                        <SelectItem key={level.value} value={level.value}>{level.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="summary">Summary *</Label>
                <Input
                  id="summary"
                  placeholder="Brief summary of this memory"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  required
                  data-testid="memory-summary-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="memory_text">Content *</Label>
                <Textarea
                  id="memory_text"
                  placeholder="Full content of the communication..."
                  value={formData.memory_text}
                  onChange={(e) => setFormData({ ...formData, memory_text: e.target.value })}
                  rows={6}
                  required
                  data-testid="memory-content-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="contact_name">Contact Name</Label>
                <Input
                  id="contact_name"
                  placeholder="e.g., Jean Dupont"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                  data-testid="memory-contact-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="keywords">Keywords (comma-separated)</Label>
                <Input
                  id="keywords"
                  placeholder="e.g., contract, pricing, deadline"
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  data-testid="memory-keywords-input"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => navigate(`/clones/${personId}/chat`)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={loading} className="gap-2" data-testid="save-memory-btn">
                  <Save className="w-4 h-4" />
                  {loading ? 'Saving...' : 'Save Memory'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
